import { compact, first, isArray, isString, uniq } from 'lodash';
import { parseISO, isValid, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { common, model } from '@gosupersimple/types';

import {
  containsISODateString,
  getDefaultFormattedLocalDate,
  DateTimeFormat,
  isDateRange,
} from '@/lib/date';
import { isSingleFilterCondition, isValueExpression } from '@/explore/pipeline/operation';
import { notNil } from '@/lib/utils';
import { isNumberType } from '@/explore/utils';
import { FieldOrGroup, findFieldWithGroup, isField, isFieldGroup } from '@/explore/pipeline/utils';
import { isNumericString } from '@/lib/utils/string';

import {
  FilterOperator,
  FilterValue,
  type CompositeFilterCondition,
  type Field,
  type FilterCondition,
} from '../../types';

const filterBinaryOperators = [
  '==',
  '!=',
  '>',
  '>=',
  '<',
  '<=',
  'in',
  'notin',
  'icontains',
  'noticontains',
  'arrcontains',
] as const;
export const filterUnaryOperators = ['istrue', 'isfalse', 'isnull', 'isnotnull'] as const;
export const filterOperators = [...filterBinaryOperators, ...filterUnaryOperators] as const;

export type FilterBinaryOperator = (typeof filterBinaryOperators)[number];
type FilterUnaryOperator = (typeof filterUnaryOperators)[number];
// UI omits some operators that the API supports
export type UIFilterOperator = FilterBinaryOperator | FilterUnaryOperator;

const FilterOperatorLabels: { [key in UIFilterOperator]: string } = {
  '==': '= (equals)',
  '!=': '≠ (does not equal)',
  '>': '> (is greater than)',
  '<': '< (is less than)',
  '>=': '≥ (is greater than or equal to)',
  '<=': '≤ (is less than or equal to)',
  in: 'is one of',
  notin: 'is not one of',
  noticontains: 'does not contain text',
  icontains: 'contains text',
  arrcontains: 'array contains',
  isnull: 'has no value (is null)',
  isnotnull: 'has a value (not null)',
  istrue: 'Yes',
  isfalse: 'No',
};

const DateFilterOperatorLabels: { [key in UIFilterOperator]: string } = {
  ...FilterOperatorLabels,
  '==': 'is',
  '!=': 'is not',
  '>': '> (is after)',
  '<': '< (is before)',
  '>=': '≥ (is on or after)',
  '<=': '≤ (is on or before)',
};

export const getOperatorLabel = (operator: UIFilterOperator, fieldType: Field['type']) =>
  fieldType === 'Date' ? DateFilterOperatorLabels[operator] : FilterOperatorLabels[operator];

export const isBinaryFilterOperator = (
  operator: UIFilterOperator | FilterOperator,
): operator is FilterBinaryOperator =>
  filterBinaryOperators.includes(operator as FilterBinaryOperator);

export const requiresArrayValue = (operator: UIFilterOperator | FilterOperator) =>
  operator === 'in' || operator === 'notin';

const isPartialNumericInput = (value: string): boolean => {
  // Incomplete inputs that should stay as strings during typing
  return (
    value === '-' ||
    value === '.' ||
    value === '-.' ||
    value.endsWith('.') || // Trailing decimal like "-0."
    /^-?0+$/.test(value) || // Just zeros with optional minus prefix
    /\.\d*0$/.test(value) // Decimal ending in zero (e.g., "1.0", "1.50")
  );
};

export const formatNumericFilterValue = (value: FilterValue): string | number => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  return isPartialNumericInput(value.toString()) ? value.toString() : Number(value);
};

export const parseFilterValue = (
  value: string,
  type: model.PropertyType | null,
  operator: UIFilterOperator,
): FilterValue | undefined => {
  if (isNumberType(type) && operator !== 'in' && !isNaN(Number(value))) {
    return formatNumericFilterValue(value);
  }
  if (requiresArrayValue(operator)) {
    const split = value.split(',').map((v) => v.trim());
    if (isNumberType(type) && split.every((v) => isNumericString(v))) {
      return split.map((v) => Number(v));
    }
    return split;
  }
  return value;
};

export const stringifyFilterValue = (value: FilterValue): string => {
  if (Array.isArray(value)) {
    return value.join(', ').trim();
  }
  return value?.toString() ?? '';
};

export const createEmptyFilterCondition = (
  field?: Field,
  value?: FilterValue,
): FilterCondition => ({
  key: field?.key ?? '',
  operator: '==',
  value: ensureStableValue(field, undefined, value ?? ''),
});

export const ensureValidDateValue = (
  value: Exclude<FilterValue, common.ExpressionValue> | undefined,
) => {
  return isString(value) && containsISODateString(value) ? value : getDefaultFormattedLocalDate();
};

export const ensureStableNumberValue = (
  value: Exclude<FilterValue, common.ExpressionValue> | undefined,
) => {
  return value === '' || isNaN(Number(value)) ? undefined : Number(value);
};

export const ensureValidStringArrayValue = (value: unknown): string[] => {
  return isArray(value)
    ? value.map(ensureValidStringValue)
    : isString(value)
      ? compact(value.split(',').map(ensureValidStringValue))
      : [];
};

export const ensureValidStringValue = (value: unknown): string => (isString(value) ? value : '');

/**
 * Ensure the value is of the correct shape and displayable in the corresponding input.
 */
export const ensureStableValue = (
  field?: Field,
  operator?: UIFilterOperator,
  value?: FilterValue,
): FilterValue | undefined => {
  if (isValueExpression(value)) {
    return value;
  }
  const requiresArray = operator !== undefined && requiresArrayValue(operator);
  switch (field?.type) {
    case 'Date':
      if (requiresArray) {
        return [ensureValidDateValue(value)];
      }

      if (operator === '==' || operator === '!=') {
        return !isDateRange(value)
          ? {
              from: ensureValidDateValue(value),
              to: ensureValidDateValue(value),
            }
          : value;
      }

      return ensureValidDateValue(isDateRange(value) ? value.from : value);
    case 'Boolean':
      return requiresArray ? uniq([value].flat().map(Boolean)) : Boolean(value);
    case 'Number':
    case 'Integer':
    case 'Float':
      if (requiresArray) {
        const valueAsArray = [value].flat().filter(notNil);
        return valueAsArray.every((v) => !isNaN(Number(v)))
          ? valueAsArray.map(Number)
          : valueAsArray.map(String);
      }
      return ensureStableNumberValue(value);
    case 'String':
      return requiresArray ? ensureValidStringArrayValue(value) : ensureValidStringValue(value);
    default:
      return requiresArray
        ? uniq(
            [value].flatMap((value) =>
              String(value)
                .split(',')
                .filter((v) => v !== ''),
            ),
          )
        : String(value);
  }
};

export const parseValueForFilter = (
  value: unknown,
  type: model.PropertyType | null,
  timezone: string,
): FilterValue => {
  const validation = common.filterValue.safeParse(value);
  const parsedValue = validation.success ? validation.data : JSON.stringify(value);
  switch (true) {
    case type === 'Date' && isString(parsedValue): {
      // Convert UTC ISO format to account timezone
      const d = parseISO(parsedValue);
      return isValid(d) ? format(toZonedTime(d, timezone), DateTimeFormat) : '';
    }
    case type === 'Boolean' && isString(parsedValue):
      return parsedValue.toLowerCase() === 'true' || parsedValue === '1';
    default:
      return parsedValue;
  }
};

export const isValidCondition = (
  condition: CompositeFilterCondition,
  fields: FieldOrGroup[],
): boolean => {
  if (isSingleFilterCondition(condition)) {
    const field = findFieldWithGroup(
      fields,
      condition.key,
      condition.relation !== undefined
        ? `${condition.relation.modelId}.${condition.relation.key}`
        : undefined,
    );

    if (field === undefined || condition.value === null) {
      return false;
    }

    if (isValueExpression(condition.value)) {
      return condition.value.expression !== undefined && condition.value.expression !== '';
    }

    if (isNumberType(field.type) && isBinaryFilterOperator(condition.operator)) {
      return requiresArrayValue(condition.operator) && Array.isArray(condition.value)
        ? condition.value.length > 0 && condition.value.every((v) => !isNaN(Number(v)))
        : !isNaN(Number(condition.value));
    }

    if (field.type === 'Date' && condition.value !== undefined) {
      if (typeof condition.value === 'string') {
        return isValid(new Date(condition.value));
      }
      if (isDateRange(condition.value)) {
        return isValid(new Date(condition.value.from)) && isValid(new Date(condition.value.to));
      }

      return false;
    }

    if (Array.isArray(condition.value) && condition.value.length === 0) {
      return false;
    }

    return true;
  }
  return condition.operands.every((c) => isValidCondition(c, fields));
};

export const getConditionField = (
  condition: FilterCondition,
  fields: FieldOrGroup[],
): Field | undefined => {
  const relation = condition.relation;
  return relation === undefined
    ? fields.filter(isField).find((field) => field.key === condition.key)
    : fields
        .filter(isFieldGroup)
        .find((group) => group.key === `${relation.modelId}.${relation.key}`)
        ?.fields.find((field) => field.key === condition.key);
};

const ensureValidConditionOrNull = (
  condition: common.CompositeFilterCondition,
  fields: FieldOrGroup[],
): common.CompositeFilterCondition | null => {
  if (isSingleFilterCondition(condition)) {
    const field = getConditionField(condition, fields);
    return field !== undefined ? condition : null;
  }
  const operands = compact(
    condition.operands.map((operand) => ensureValidConditionOrNull(operand, fields)),
  );
  return operands.length > 0 ? { ...condition, operands } : null;
};

export const createDefaultFilterCondition = (
  fields: FieldOrGroup[],
  previousCondition?: common.CompositeFilterCondition,
) => {
  const validatedPrevious =
    previousCondition !== undefined
      ? ensureValidConditionOrNull(previousCondition, fields)
      : undefined;
  return validatedPrevious ?? createEmptyFilterCondition(first(fields.filter(isField)));
};
